import {
  Activity,
  Banknote,
  BellRing,
  BookOpen,
  Bug,
  Building2,
  CalendarClock,
  CalendarDays,
  CalendarRange,
  ChartColumn,
  ClipboardCheck,
  ClipboardList,
  ClipboardPen,
  FileClock,
  FilePenLine,
  Gauge,
  HandCoins,
  Hourglass,
  IdCard,
  Inbox,
  Lightbulb,
  ListChecks,
  ListPlus,
  MapPin,
  MessageCircleQuestion,
  MessageSquare,
  NotebookPen,
  OctagonMinus,
  Package,
  PencilLine,
  ReceiptText,
  Signpost,
  Truck,
  UserCog,
  UserRound,
  Users,
  Wallet,
  Wrench,
} from "lucide-react";
import type { ComponentType } from "react";

type Icon = ComponentType<{ className?: string; strokeWidth?: number; "aria-hidden"?: boolean }>;

/** A small flat-top tower crane on lucide's 24 px grid (lucide has no crane). */
function TowerCraneIcon({
  className,
  strokeWidth = 2,
}: {
  className?: string;
  strokeWidth?: number;
}) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <path d="M2 5h20M2 8h20M2 5v3M22 5v3M3 8l2-3 2 3 2-3 2 3" />
      <path d="M13 8v13M16 8v13M13 12.5h3M13 16.5h3M11 21h7" />
      <path d="M19 8v3h3V8M7 8v6a1.5 1.5 0 1 1-1.5 1.5" />
    </svg>
  );
}

/**
 * A line icon per tool, in De Ceuster's style. Every tool DC Data serves has
 * its own; the wrench is left for a tool not listed, such as one it no
 * longer serves.
 */
const TOOL_ICONS: Record<string, Icon> = {
  whoami: UserRound,
  describe_model: BookOpen,
  search_clients: Users,
  get_client: Building2,
  list_cranes: TowerCraneIcon,
  search_orders: ClipboardList,
  search_invoices: ReceiptText,
  order_locations: MapPin,
  revenue_summary: ChartColumn,
  crane_contracts: FileClock,
  search_payments: Banknote,
  search_products: Package,
  add_client_note: NotebookPen,
  draft_quote: FilePenLine,
  schedule_activity: CalendarClock,
  crane_occupancy: Gauge,
  occupancy_alerts: BellRing,
  crane_availability: CalendarRange,
  client_overview: IdCard,
  client_payment_profile: HandCoins,
  unbilled_work: Hourglass,
  transport_price_check: Truck,
  connector_usage: Activity,
  connector_users: UserCog,
  // The submissions to Velta, and the tools of the dashboard's roles.
  report_gap: MessageCircleQuestion,
  submit_idea: Lightbulb,
  my_submissions: Inbox,
  roadmap_month: CalendarDays,
  roadmap_item: Signpost,
  update_roadmap_item: PencilLine,
  roadmap_comment: MessageSquare,
  roadmap_blocker: OctagonMinus,
  set_roadmap_budget: Wallet,
  create_roadmap_item: ListPlus,
  list_triage: ClipboardCheck,
  triage_entry: ListChecks,
  tool_issues: Bug,
  update_tool_issue: ClipboardPen,
};

interface ToolIconProps {
  /** The tool's name, such as `list_cranes`. */
  name: string;
  className?: string;
}

/** The tool's line icon, decorative: the row says what the tool is. */
export function ToolIcon({ name, className }: ToolIconProps) {
  const Icon = Object.hasOwn(TOOL_ICONS, name) ? (TOOL_ICONS[name] ?? Wrench) : Wrench;
  return <Icon aria-hidden className={className} strokeWidth={1.75} />;
}
